import { createSelector } from 'reselect'
import { isFeatureEnabled } from 'Optimizely'

const simpleSelector = (sel) => createSelector([sel], (x) => x)

const _mfa = simpleSelector((state) => state.mfa)
const _profile = simpleSelector((state) => state.profile)
const _authenticityVerificationState = simpleSelector((state) => state.authenticityVerificationState)
const _config = simpleSelector((state) => state.config)
const _driving = simpleSelector((state) => state.driving)
const _taxes = simpleSelector((state) => state.taxes)
const _prop22EarningsStatement = simpleSelector((state) => state.prop22EarningsStatement)
const _nycEarningsStatement = simpleSelector((state) => state.nycEarningsStatement)
const _seattleDriver = simpleSelector((state) => state.seattleDriver)
const _accountSettings = simpleSelector((state) => state.accountSettings)
const _isLocked = simpleSelector((state) => state.isLocked)
const _lockReason = simpleSelector((state) => state.lockReason)
const _flags = simpleSelector((state) => state.flags?.[0] || {})
const _termsDocuments = simpleSelector((state) => state.termsDocuments)
const _disclosureDocuments = simpleSelector((state) => state.disclosureDocuments)

const isRegisteredDriver = (profile) => profile?.certifications?.includes('basic_driver') ?? false

export const globalStyleSelector = createSelector([_config], (config) => ({
  sidebarOpen: !!config.sidebarOpen,
  modalOpen: !!config.modalOpen,
}))

export const mediaSizeSelector = createSelector([_config], (config) => ({ mediaSize: config.mediaSize }))

export const gigHistorySelector = createSelector(
  [_driving, _prop22EarningsStatement, _nycEarningsStatement, _seattleDriver, _config],
  (driving, prop22EarningsStatement, nycEarningsStatement, seattleDriver, config) => {
    const lastFetchedPage =
      driving.history &&
      Object.keys(driving.history)
        .map(Number)
        .sort((a, b) => b - a)[0]
    const hasMoreHistory = lastFetchedPage && driving.history[lastFetchedPage].length === 25
    const nextHistoryPage = hasMoreHistory ? lastFetchedPage + 1 : null

    return {
      summary: driving.summary,
      history: driving.history,
      nextHistoryPage,
      prop22EarningsStatement,
      nycEarningsStatement,
      seattleDriver,
      mediaSize: config.mediaSize,
    }
  }
)

export const taxSelector = createSelector([_taxes, _profile], (taxes, profile) => ({ taxes, profile }))

export const basicInfoSelector = createSelector([_config, _profile, _isLocked], (config, profile, isLocked) => ({
  registered: isRegisteredDriver(profile),
  profile,
  isWebview: config.isWebview,
  isLocked,
}))

export const emailAndPhoneSelector = createSelector([_profile, _isLocked, _flags], (profile, isLocked, flags) => ({
  profile,
  isLocked,
  flags,
}))

export const passwordFormSelector = createSelector([_config, _profile, _isLocked], (config, profile, isLocked) => ({
  profile,
  isWebview: config.isWebview,
  isLocked,
}))

export const termsSelector = createSelector(
  [_config, _profile, _termsDocuments],
  (config, profile, termsDocuments) => ({
    profile,
    isWebview: config.isWebview,
    mediaSize: config.mediaSize,
    termsDocuments,
  })
)

export const backgroundSelector = createSelector([_profile, _disclosureDocuments], (profile, disclosureDocuments) => ({
  profile,
  disclosureDocuments,
}))

export const isWebviewSelector = createSelector([_config], (config) => ({
  isWebview: config.isWebview,
}))

export const sidebarOpenSelector = createSelector([_config], (config) => ({ sidebarOpen: config.sidebarOpen }))

export const profileSelector = createSelector([_profile], (profile) => ({ profile }))

export const signOutSelector = createSelector([_profile, _config], (profile, config) => ({
  profile,
  appRootPath: config.appRootPath,
}))

export const historyWatcherSelector = createSelector([_profile, _config], (profile, config) => ({
  profile,
  appRootPath: config.appRootPath,
}))

export const disclosuresSelector = createSelector([_config, _disclosureDocuments], (config, disclosureDocuments) => ({
  disclosureDocuments,
  isWebview: config.isWebview,
  mediaSize: config.mediaSize,
}))

export const driverNeeds = (mfa, profile, lockReason, isWebview, state, termsDocuments, disclosureDocuments) => {
  const registered = isRegisteredDriver(profile)

  if (mfa.guid) {
    return 'mfa'
  }
  if (!profile) {
    return 'user'
  }
  if (!profile.phone_number_verified) {
    return 'verify-phone'
  }
  const terms = termsDocuments?.documents ?? []
  if (terms.some((doc) => doc.required)) {
    return 'terms'
  }
  if (!profile.hashed_date_of_birth) {
    return 'basic-info'
  }
  // We key off of both reason and subreason just in case the API changes in the future. But it should just be subreason.
  if (lockReason?.subreason === 'duplicate_account' || lockReason?.reason === 'duplicate_profile') {
    return 'duplicate-account'
  }
  if (!profile.drivers_license || !profile.drivers_license.hashed_number) {
    return 'driving-info'
  }
  if (profile.status === 'pending') {
    return 'blocked-message'
  }
  if (!isWebview && !profile.has_ssn) {
    console.log('go-mobile', { isWebview, profile, state })
    return 'go-mobile'
  }
  if (
    isWebview &&
    profile.status === 'onboarding' &&
    (state === 'unsubmitted' || state === 'reattempt' || state === 'failed') &&
    !registered
  ) {
    console.log('verify-identity', { isWebview, profile, state })
    return 'verify-identity'
  }
  const newComplianceFlowEnabled = isFeatureEnabled('new-compliance-flow', profile)
  if (!profile.has_ssn && !newComplianceFlowEnabled) {
    return 'consumer-report'
  }
  const disclosures = disclosureDocuments?.documents ?? []
  if (disclosures.some((doc) => doc.required) && newComplianceFlowEnabled) {
    return 'disclosures'
  }
  if (!profile.has_ssn && newComplianceFlowEnabled) {
    return 'ssn'
  }
  if (!profile.has_bank_account) {
    return 'bank-info'
  }
  if (profile.status !== 'locked' && !profile.has_avatar && !profile.verified_avatar_url) {
    return 'profile-picture'
  }
  if (!registered) {
    return 'onboarding'
  }
  if (profile.profile_settings && profile.profile_settings.disable_profile_edit) {
    return 'restricted-access'
  }

  return null
}

export const driveNeedsSelector = createSelector(
  [
    _mfa,
    _profile,
    _config,
    _authenticityVerificationState,
    _accountSettings,
    _lockReason,
    _termsDocuments,
    _disclosureDocuments,
  ],
  (mfa, profile, config, state, accountSettings, lockReason, termsDocuments, disclosureDocuments) => {
    const { wasSigningUp, isWebview } = config

    const registered = isRegisteredDriver(profile)

    return {
      registered,
      wasSigningUp,
      isWebview,
      mfa,
      profile,
      authenticityVerificationState: state,
      flash: config.flash,
      accountSettings,
      lockReason,
      termsDocuments,
      disclosureDocuments,
    }
  }
)

export const driveSidebarSelector = createSelector(
  [_profile, _config, _prop22EarningsStatement, _nycEarningsStatement, _seattleDriver, _isLocked],
  (profile, config, prop22EarningsStatement, nycEarningsStatement, seattleDriver, isLocked) => ({
    profile,
    prop22EarningsStatement,
    nycEarningsStatement,
    appRootPath: config.appRootPath,
    seattleDriver,
    isLocked,
  })
)

export const driveDashboardSelector = createSelector([_profile, _config], (profile, config) => ({
  profile,
  badges: config.badges,
}))
